import { useState, useRef, useEffect } from "react";
import { ToolsShell, SensorNotice, primaryBtn, TOOLS_ACCENT } from "./ToolsShell.jsx";

// Roof Pitch Calculator — specialized inclinometer for roof pitches.
// Shows three formats at once: degrees, percent slope, and roof-pitch
// fraction (e.g. "6/12" = 6 inches rise per 12 inches run).
//
// Use case: hold phone flush against the underside of a rafter, against
// a shingle line, or lay on the roof itself. Reads the angle from
// horizontal.

const PITCH_NAMES = {
  // common roof-pitch nicknames at /12 run
  2: "Low slope",
  4: "Conventional low",
  6: "Standard",
  8: "Steep",
  9: "Steep",
  10: "Very steep",
  12: "Steep (45°)",
};

export default function RoofPitch() {
  const [state, setState] = useState("idle");
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [calib, setCalib] = useState(0);
  const handler = useRef(null);

  const enable = async () => {
    if (typeof DeviceOrientationEvent === "undefined") { setState("unsupported"); return; }
    try {
      if (typeof DeviceOrientationEvent.requestPermission === "function") {
        const r = await DeviceOrientationEvent.requestPermission();
        if (r !== "granted") { setState("denied"); return; }
      }
      handler.current = (e) => {
        setTilt({ x: e.gamma || 0, y: e.beta || 0 });
      };
      window.addEventListener("deviceorientation", handler.current, true);
      setState("ok");
    } catch {
      setState("denied");
    }
  };

  useEffect(() => () => {
    if (handler.current) window.removeEventListener("deviceorientation", handler.current, true);
  }, []);

  // Use whichever axis is larger — that's the dominant tilt direction
  const rawAngle = Math.abs(tilt.y) > Math.abs(tilt.x) ? tilt.y : tilt.x;
  const angle = Math.abs(rawAngle - calib);

  const percent = Math.tan(angle * Math.PI / 180) * 100;
  // Rise/12 run; round to nearest 0.5 for display
  const rise = 12 * Math.tan(angle * Math.PI / 180);
  const rise05 = Math.round(rise * 2) / 2;
  const pitchName = PITCH_NAMES[Math.round(rise05)] || (rise05 < 2 ? "Nearly flat" : rise05 > 12 ? "Steeper than 12/12" : "");

  return (
    <ToolsShell title="Roof Pitch">
      {state !== "ok" && (
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <p style={{ fontSize: 14, color: "#C8CADF", marginBottom: 18 }}>
            Measure roof pitch in degrees, percent, and rise/run. Hold phone flush against the roof or rafter and read.
          </p>
          <button onClick={enable} style={primaryBtn}>Start</button>
          <SensorNotice state={state === "idle" ? "prompt" : state} what="Motion sensor" />
        </div>
      )}

      {state === "ok" && (
        <>
          {/* Triangle diagram */}
          <div style={{ display: "flex", justifyContent: "center" }}>
            <svg width="280" height="170" viewBox="0 0 280 170">
              <line x1="20" y1="150" x2="260" y2="150" stroke="#262839" strokeWidth="2" />
              <g transform={`rotate(${-Math.min(45, angle)} 20 150)`}>
                <line x1="20" y1="150" x2="220" y2="150" stroke={TOOLS_ACCENT} strokeWidth="3" strokeLinecap="round" />
              </g>
              <line x1="220" y1="150"
                    x2="220" y2={150 - 200 * Math.sin(Math.min(45, angle) * Math.PI / 180)}
                    stroke="#5B5E7A" strokeWidth="1" strokeDasharray="2 3" />
              <text x="140" y="165" fontSize="11" fill="#7C8099" textAnchor="middle">run = 12</text>
              <text x="226" y={150 - 30} fontSize="11" fill="#7C8099">rise</text>
              <circle cx="20" cy="150" r="4" fill={TOOLS_ACCENT} />
            </svg>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginTop: 6 }}>
            <Card label="Pitch" value={`${rise05}/12`} sub={pitchName} />
            <Card label="Degrees" value={`${angle.toFixed(1)}°`} />
            <Card label="Slope" value={`${percent.toFixed(1)}%`} />
          </div>

          <div style={{ marginTop: 16, display: "flex", gap: 8, justifyContent: "center" }}>
            <button onClick={() => setCalib(rawAngle)} style={{ background: "#1C1E2E", color: TOOLS_ACCENT, border: "none", borderRadius: 8, padding: "10px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              Calibrate (set zero here)
            </button>
            {calib !== 0 && (
              <button onClick={() => setCalib(0)} style={{ background: "#1C1E2E", color: "#9A9DBE", border: "none", borderRadius: 8, padding: "10px 14px", fontSize: 13, cursor: "pointer" }}>
                Reset
              </button>
            )}
          </div>

          <div style={{ marginTop: 16, padding: "12px 14px", background: "#13141F", border: "1px solid #262839", borderRadius: 10, fontSize: 12, color: "#C8CADF" }}>
            <strong style={{ color: "#E8E9F3" }}>Quick reference:</strong><br/>
            2/12 = low slope (rolled roofing) · 4/12 = conventional low · 6/12 = standard residential (~26.6°) · 8/12 = steep (~33.7°) · 12/12 = 45°
          </div>
        </>
      )}
    </ToolsShell>
  );
}

function Card({ label, value, sub }) {
  return (
    <div style={{ background: "#13141F", border: `1px solid ${TOOLS_ACCENT}33`, borderRadius: 10, padding: "14px 8px", textAlign: "center" }}>
      <div style={{ fontSize: 11, color: "#7C8099", textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: TOOLS_ACCENT, marginTop: 4, fontVariantNumeric: "tabular-nums" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "#9A9DBE", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
